/**
 * 教学中心 - 提示词技巧教程
 */
import React, { useState } from "react";
import {
  BookOpen,
  Play,
  ChevronDown,
  Lightbulb,
  Camera,
  Film,
  Sparkles,
  Search,
  ArrowRight,
  Grid3X3,
} from "lucide-react";
import { useTheme } from "../../context/ThemeContext";

// 教程内容数据
const TUTORIAL_CONTENT = {
  title: "Prompt 技巧",
  subtitle: "海螺 AI 视频生成 Prompt 写作指南",
  lastUpdated: "2024",

  sections: [
    {
      id: "basic-formula",
      title: "提示词基础工程",
      icon: <Lightbulb size={18} />,
      content: `
### Prompt 基础公式

适用于对于视频没有明确的镜头呈现需求，或者期待使用AI视频激发创作灵感的朋友，自由的 Prompt 可以获得更具想象力的画面。

> **Prompt基础公式 = 要创建的主要表现物 + 场景空间 + 运动 / 变化**

**公式说明：**
- **要创建的主要表现物**：视频的核心信息，可以是人物、动物、物体或想象之物
- **场景空间**：表现物的周围环境，可以是精确的标志性空间或幻想性场景
- **运动/变化**：主要表现物在视频中的状态描述

**示例：**
1. 一只小狗在公园中奔跑
2. 一个女人打着伞在雨中的街头行走
3. 山谷中的一条小溪静静流淌
      `,
    },
    {
      id: "precise-formula",
      title: "Prompt 精确公式",
      icon: <Camera size={18} />,
      content: `
### Prompt 精确公式

适用于对于镜头运动或画面呈现有明确需求，需要更加专业视频输出物的用户。

> **Prompt 精确公式 = 要创建的主要表现物 + 场景空间 + 运动 / 变化 + 镜头运动 + 美感氛围**

**公式说明：**
- **镜头运动**：限定视频画面的呈现方式（推、拉、摇、移、升、降）
- **美感氛围**：限定画面视觉风格和氛围感

**示例：**
1. 一对情侣坐在公园的长椅上交流，镜头维持固定拍摄情侣，画面色调偏暖，氛围温馨
2. 一只小羊在一片草地里低头吃草，镜头缓缓推进小羊，画面色调自然写实
3. 一个身穿西装的男人面色凝重地在面馆里吃面，镜头逐渐拉远展示面馆的吵闹环境
      `,
    },
    {
      id: "tips",
      title: "公式使用技巧",
      icon: <Sparkles size={18} />,
      content: `
### Prompt 公式的使用技巧

上述两类 Prompt 公式并非是严格的使用准则，与AI的每一次对话都可能产出不同的效果。但总体遵循两个基本原则：

1. **更精确的表达可以带来更准确的视频信息呈现**
2. **更丰富的表达可以带来更好的视频生成效果**
      `,
    },
    {
      id: "camera-control",
      title: "精准的镜头控制",
      icon: <Film size={18} />,
      content: `
### 精准的镜头控制

海螺AI能够有效响应多种类型的镜头运动，为了实现更加精准和更具美感的镜头运动，可参考以下技巧：

1. **为镜头运动增加更精确的时序**：可以获得更加精确、更大镜头动态的视频
   - ✗ "镜头旋转下降"
   - ✓ "镜头先缓缓下降，之后在下降的过程中向右环绕"

2. **为镜头运动带来的画面变化增加更细致的描述**：可以获得更精确、更具美感的镜头运动画面
   - ✗ "镜头从下往上拍摄长颈鹿"
   - ✓ "镜头由长颈鹿的脚部特写开始，缓缓上升，拍摄到长颈鹿的身体，最后停留在长颈鹿的面部"

3. **将镜头运动的时长合理控制在5-6秒之内**：避免过度复杂的镜头导致生成失败
      `,
    },
    {
      id: "aesthetics-control",
      title: "精准的美学控制",
      icon: <Grid3X3 size={18} />,
      content: `
### 精准的美学控制

海螺AI能够准确识别Prompt中的美感和氛围感描述，相同的基础Prompt通过不同的美感氛围控制可以呈现截然不同的效果。

| 对比示例 |
|:------|
| 镜头拍摄一个女性坐在咖啡馆里，女人抬头思考着，镜头缓缓移动拍摄到窗外的街道，画面色调灰暗，色彩低饱和，氛围阴郁 |
| 镜头拍摄一个女性坐在咖啡馆里，女人抬头看着窗外，镜头缓缓移动拍摄到窗外的街道，画面呈现暖色调，色彩浓郁，氛围轻松惬意 |
      `,
      hasVideos: true,
      videos: [
        { url: "https://filecdn.minimax.chat/public/ab398d29-2003-473f-80a4-9aef43003791.mp4", prompt1: true },
        { url: "https://filecdn.minimax.chat/public/bad229ef-99ab-4a9b-b8d6-745c6bfda598.mp4", prompt2: true },
      ],
    },
    {
      id: "img2video",
      title: "图生视频教程",
      icon: <Play size={18} />,
      content: `
### 图生视频 Prompt 基础公式

图生视频使用的图片作为视频的首帧出现，限定了视频的主体物形象和基本画面美学风格，因此相比文生视频Prompt可以适当减少信息量。

> **图生视频Prompt基础公式 = 首帧中的主要表现物 + 运动 / 变化**

**示例：**
| Prompt | 图片 | 视频 |
|:-------|:----|:-----|
| 画面中的小狗狗，眼中发出蓝光，小狗狗面前凌乱的衣服也发出蓝光慢慢飘起，然后自动在空中折叠整齐，落在小狗狗面前，之后小狗狗眼中的蓝光消失了 | ![示例1](https://filecdn.minimax.chat/public/f7296858-5529-407f-8f81-498454b2da70.png) | 视频 |
| 画面中的蓝色毛绒生物不断搅动着面前的汤锅，汤锅冒着热气，之后蓝色怪物对着面前的锅吹起，面前的汤碗结成冰块 | ![示例2](https://filecdn.minimax.chat/public/09db3664-7727-42eb-aefe-d9b32f391642.png) | 视频 |
      `,
    },
  ],
};

// 视频播放器组件
const VideoPlayer = ({ src, poster, className = "" }) => {
  const [, setIsLoading] = useState(true);

  return (
    <div
      className={`relative rounded-lg overflow-hidden bg-black ${className}`} /* 视频播放器底色：媒体语境，保留 */
      style={{ aspectRatio: "16/9" }}
    >
      <video
        src={src}
        poster={poster}
        controls
        className="w-full h-full"
        onLoadedData={() => setIsLoading(false)}
      />
    </div>
  );
};

// 提示框组件
const Callout = ({ icon, color = "var(--color-accent-primary)", children }) => {
  return (
    <div
      className="rounded-lg p-4 my-4"
      style={{
        backgroundColor: `color-mix(in srgb, ${color} 8%, transparent)`,
        borderLeft: `3px solid ${color}`,
      }}
    >
      <div className="flex items-start gap-3">
        <span className="text-lg" style={{ color }}>{icon}</span>
        <div className="flex-1 text-sm leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
          {children}
        </div>
      </div>
    </div>
  );
};

// 展开/折叠区块
const ExpandableSection = ({ section, isOpen, onToggle, isActive }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();

  return (
    <div
      className="rounded-xl overflow-hidden transition-colors duration-300"
      style={{
        backgroundColor: 'var(--color-bg-secondary)',
        border: `1px solid ${isActive ? theme.colors.accentPrimary : 'var(--color-border-primary)'}`,
      }}
    >
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between text-left"
        style={{
          backgroundColor: isActive ? `${theme.colors.accentPrimary}10` : 'transparent',
        }}
      >
        <div className="flex items-center gap-3">
          <span style={{ color: isActive ? theme.colors.accentSecondary : 'var(--color-text-muted)' }}>
            {section.icon}
          </span>
          <span className="text-sm font-bold" style={{ color: 'var(--color-text-primary)' }}>
            {section.title}
          </span>
        </div>
        <ChevronDown
          size={16}
          className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
          style={{ color: 'var(--color-text-muted)' }}
        />
      </button>

      {/* grid-template-rows 0fr→1fr 实现真实展开动画（内容常驻 DOM，高度参与过渡） */}
      <div
        className="grid transition-[grid-template-rows] duration-300"
        style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          <div className="px-4 pb-4 border-t" style={{ borderColor: 'var(--color-border-primary)' }}>
            <div className="pt-4 prose prose-sm max-w-none" style={{ color: 'var(--color-text-secondary)' }}>
              <ContentRenderer content={section.content} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// 简单的 Markdown 解析渲染
const ContentRenderer = ({ content }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const lines = content.trim().split("\n");
  const elements = [];
  let keyCounter = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // 标题
    if (line.startsWith("### ")) {
      elements.push(
        <h4 key={keyCounter++} className="text-sm font-bold mt-4 mb-2" style={{ color: 'var(--color-text-primary)' }}>
          {line.replace("### ", "")}
        </h4>
      );
      continue;
    }

    // 二级标题
    if (line.startsWith("## ")) {
      elements.push(
        <h3 key={keyCounter++} className="text-base font-bold mt-5 mb-3" style={{ color: 'var(--color-text-primary)' }}>
          {line.replace("## ", "")}
        </h3>
      );
      continue;
    }

    // 引用块
    if (line.startsWith("> ")) {
      let quote = line.replace("> ", "");
      let j = i + 1;
      while (j < lines.length && lines[j].startsWith("> ")) {
        quote += "\n" + lines[j].replace("> ", "");
        j++;
      }
      elements.push(
        <blockquote
          key={keyCounter++}
          className="border-l-2 pl-4 my-3 text-sm italic"
          style={{
            borderColor: 'var(--color-accent-primary)',
            color: 'var(--color-text-muted)',
          }}
        >
          {quote}
        </blockquote>
      );
      i = j - 1;
      continue;
    }

    // 无序列表
    if (line.startsWith("- ")) {
      const listItems = [];
      let j = i;
      while (j < lines.length && lines[j].startsWith("- ")) {
        let item = lines[j].substring(2);
        let k = j + 1;
        while (k < lines.length && !lines[k].startsWith("- ") && !lines[k].startsWith("#") && lines[k].trim()) {
          item += " " + lines[k].trim();
          k++;
        }
        listItems.push(item);
        j = k;
      }
      elements.push(
        <ul key={keyCounter++} className="list-disc list-inside my-3 space-y-1 text-sm">
          {listItems.map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ul>
      );
      i = j - 1;
      continue;
    }

    // 有序列表
    if (/^\d+\. /.test(line)) {
      const listItems = [];
      let j = i;
      while (j < lines.length && /^\d+\. /.test(lines[j])) {
        let item = lines[j].replace(/^\d+\. /, "");
        let k = j + 1;
        while (k < lines.length && !lines[k].match(/^\d+\./) && !lines[k].startsWith("#") && lines[k].trim()) {
          item += " " + lines[k].trim();
          k++;
        }
        listItems.push(item);
        j = k;
      }
      elements.push(
        <ol key={keyCounter++} className="list-decimal list-inside my-3 space-y-1 text-sm">
          {listItems.map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ol>
      );
      i = j - 1;
      continue;
    }

    // 示例框
    if (line.startsWith("> **") || line.startsWith("> ")) {
      const examples = [];
      let j = i;
      while (j < lines.length && lines[j].startsWith(">")) {
        let example = lines[j].replace(/> \*\*|:\*\*|>/, "").trim();
        let k = j + 1;
        while (k < lines.length && !lines[k].startsWith(">") && !lines[k].startsWith("#") && lines[k].trim()) {
          example += " " + lines[k].trim();
          k++;
        }
        examples.push(example);
        j = k;
      }
      elements.push(
        <div key={keyCounter++} className="space-y-2 my-3">
          {examples.map((ex, idx) => (
            <div key={idx} className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              {ex}
            </div>
          ))}
        </div>
      );
      i = j - 1;
      continue;
    }

    // 表格行
    if (line.startsWith("|")) {
      const rows = [];
      let j = i;
      while (j < lines.length && lines[j].startsWith("|")) {
        rows.push(lines[j]);
        j++;
      }
      elements.push(
        <div key={keyCounter++} className="overflow-x-auto my-4">
          <table className="w-full text-xs border-collapse">
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx}>
                  {row.split("|").filter(cell => !cell.match(/^[\s-]+$/)).map((cell, cidx) => (
                    <td
                      key={cidx}
                      className="p-2 border"
                      style={{
                        borderColor: 'var(--color-border-primary)',
                        backgroundColor: idx === 0 ? `${theme.colors.accentPrimary}10` : 'transparent',
                      }}
                    >
                      {cell.trim()}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      i = j - 1;
      continue;
    }

    // 空行
    if (!line.trim()) {
      continue;
    }

    // 普通段落
    let paragraph = line;
    let j = i + 1;
    while (j < lines.length && lines[j].trim() && !lines[j].startsWith("#")) {
      paragraph += " " + lines[j].trim();
      j++;
    }
    elements.push(
      <p key={keyCounter++} className="text-sm leading-relaxed my-2" style={{ color: 'var(--color-text-secondary)' }}>
        {paragraph}
      </p>
    );
    i = j - 1;
  }

  return <>{elements}</>;
};

// 搜索组件
const SearchBar = ({ value, onChange, placeholder }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();

  return (
    <div className="relative">
      <Search
        size={14}
        className="absolute left-3 top-1/2 -translate-y-1/2"
        style={{ color: 'var(--color-text-muted)' }}
      />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full pl-9 pr-4 py-2 rounded-lg text-sm outline-none"
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          border: '1px solid var(--color-border-primary)',
          color: 'var(--color-text-primary)',
        }}
      />
    </div>
  );
};

const EducationCenter = () => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const [activeSection, setActiveSection] = useState(TUTORIAL_CONTENT.sections[0].id);
  const [expandedSections, setExpandedSections] = useState(
    TUTORIAL_CONTENT.sections.reduce((acc, s) => ({ ...acc, [s.id]: true }), {})
  );
  const [searchQuery, setSearchQuery] = useState("");

  const toggleSection = (id) => {
    setExpandedSections((prev) => ({ ...prev, [id]: !prev[id] }));
    setActiveSection(id);
  };

  // 过滤章节
  const filteredSections = TUTORIAL_CONTENT.sections.filter((section) =>
    section.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
      {/* Header */}
      <div className="px-6 py-4 border-b shrink-0" style={{ borderColor: 'var(--color-border-primary)' }}>
        <div className="flex items-center gap-3 mb-2">
          <div
            className="p-2 rounded-lg"
            style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
          >
            <BookOpen size={20} style={{ color: 'var(--color-text-secondary)' }} />
          </div>
          <div>
            <h1 className="text-lg font-bold" style={{ color: 'var(--color-text-primary)' }}>
              {TUTORIAL_CONTENT.title}
            </h1>
            <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
              {TUTORIAL_CONTENT.subtitle}
            </p>
          </div>
        </div>
        <SearchBar
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="搜索教程内容..."
        />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
        {filteredSections.length === 0 ? (
          <div className="text-center py-10" style={{ color: 'var(--color-text-muted)' }}>
            <Search size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">没有找到相关内容</p>
          </div>
        ) : (
          filteredSections.map((section) => (
            <ExpandableSection
              key={section.id}
              section={section}
              isOpen={expandedSections[section.id]}
              onToggle={() => toggleSection(section.id)}
              isActive={activeSection === section.id}
            />
          ))
        )}

        {/* 底部提示 */}
        <div
          className="mt-6 p-4 rounded-xl"
          style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)' }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb size={14} style={{ color: theme.colors.warning }} />
            <span className="text-xs font-bold" style={{ color: 'var(--color-text-secondary)' }}>
              提示
            </span>
          </div>
          <p className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>
            以上技巧适用于海螺 AI 视频生成模型。不同模型可能有不同的响应特点，建议多尝试找到最佳效果。
          </p>
        </div>
      </div>
    </div>
  );
};

export default EducationCenter;
